import {
    Component, Output, EventEmitter, HostListener, ViewChild, ElementRef, Input, AfterViewChecked, inject
} from "@angular/core";

import { ISelectedFile } from "./interface/selected-file";
import {
    FileUtil, MimeType
} from "@shopliftr/common-js/data";
import { NotificationService } from "../../notification/services/notification.service";
import { ComponentException } from "../../exceptions/component-exception";

import { MatButton } from "@angular/material/button";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
@Component({
    selector: "mgd-file-selector",
    templateUrl: "./file-selector.component.html",
    styleUrls: ["./file-selector.component.scss"],
    imports: [MatButton]
})

/**
 * Component which allows user to select a single File
 *
 * The file can either be selected in the following ways
 *  By clicking the button in the component and selecting a file in the file selector dialog
 *  By pasting a file from the clipboard
 *
 * Outputs an ISelectedFile with the File and URL
 */
export class FileSelectorComponent implements AfterViewChecked {

    @ViewChild("fileSelector") fileSelector: ElementRef<HTMLInputElement>;

    /**
     * The accepted mime types for the selector.
     *
     * If not provided all mime types are accepted. Accepts a readonly array so
     * constants such as `Files.ACCEPTED_IMAGE_TYPES` can be passed without a cast.
     */
    @Input() acceptedTypes?: Array<MimeType> | ReadonlyArray<MimeType>;

    /**
     * Label for the select button.
     *
     * This component selects *files*, not images specifically, so the default is
     * generic. Consumers that select images should pass their own label.
     */
    @Input() buttonLabel = "Select File";

    /**
     * If true, the component will listen for paste events and if an accepted file is pasted
     * accepted files it will emit the event.
     *
     */
    @Input() listenForPaste?: boolean;

    /**
     * Output the selected file
     */
    @Output() selected: EventEmitter<ISelectedFile> = new EventEmitter<ISelectedFile>();

    /**
     * If hidden is true, the button will not be displayed.
     * This is used to programmatically open a file dialog
     */
    @Input() hidden?: boolean;

    private _openFileSelectorDialog = false;

    private readonly _notificationService = inject(NotificationService);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    /**
     * On paste event handler.
     *
     * Stays silent when the paste landed in an editable element - a paste into that field
     * belongs to the field, not to this selector, since the listener is document-scoped and
     * sees every paste on the page. Otherwise an accepted file is emitted the same as one
     * chosen through the button, and anything else is reported - a paste that cannot be used
     * must say so (AOS-4505).
     */
    @HostListener("document:paste", ["$event"])
    onPaste(event: ClipboardEvent): void {

        if (!this.listenForPaste || this._isEditableTarget(event?.target)) {
            return;
        }

        const files = event.clipboardData?.files;
        const file = this._findAcceptedFile(files);

        if (file) {
            this._emitSelected(file);
        }
        else {
            this._notifyPasteRejected(files);
        }
    }

    ngAfterViewChecked(): void {

        if (this._openFileSelectorDialog) {
            // This will open the file selector after we are sure that the view is fully rendered correctly
            // This will include setting acceptedFileTypes
            this.fileSelector?.nativeElement?.click();
            this._openFileSelectorDialog = false;
        }
    }

    /**
     * Event handler for the file selector input
     *
     * It emits the onSelect event
     */
    onFileSelectorChange(): void {

        const files = this.fileSelector.nativeElement.files;
        const file = this._findAcceptedFile(files);

        if (file) {
            this._emitSelected(file);
        }
        else if (files?.length) {
            this._reportRejection(this._rejectionMessage("Could not select file", files[0].type));
        }
    }

    /**
     * Opens the file selector dialog
     *
     * Called on the click event of the select file button
     */
    openFileSelectorDialog(): void {

        this._openFileSelectorDialog = true;
    }

    /**
     * True when a paste landed somewhere the user was plausibly typing into, not aimed at
     * this selector - the control only complains about a paste it was plausibly the target
     * of.
     */
    private _isEditableTarget(target?: EventTarget | null): boolean {

        if (!(target instanceof HTMLElement)) {
            return false;
        }
        return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
    }

    /**
     * Finds the first accepted file in a FileList, if any.
     *
     * Acceptance is decided from the file's own `type` first, since a clipboard file
     * frequently has no meaningful name - `FileUtil.getMimeType(file.name)` is an extension
     * lookup, and falls back to it only when the browser supplies no type at all.
     *
     * `acceptedTypes === undefined` (unrestricted, as documented on the input) is checked
     * explicitly rather than via `!this.acceptedTypes?.length` - the latter also treats an
     * empty* array as unrestricted, the opposite of how an empty allow-list reads, and the
     * opposite of what a consumer binding `acceptedTypes` to something computed would expect.
     */
    private _findAcceptedFile(files?: FileList | null): File | undefined {

        return Array.from(files ?? [])
            .find((file) => this.acceptedTypes === undefined
                || this.acceptedTypes.includes((file.type as MimeType) || FileUtil.getMimeType(file.name)));
    }

    private _emitSelected(file: File): void {

        this.selected.emit({
            file: file,
            url: URL.createObjectURL(file)
        });
    }

    /**
     * Reports a rejected paste - either an unaccepted file, or no file at all. Both are
     * reported: a paste that cannot be used must say so (AOS-4505).
     */
    private _notifyPasteRejected(files?: FileList | null): void {

        if (!files?.length) {
            this._reportRejection("Could not paste image: the clipboard does not contain an image.");
            return;
        }
        this._reportRejection(this._rejectionMessage("Could not paste image", files[0].type));
    }

    /** The accepted formats, derived from `acceptedTypes` rather than hard-coded. */
    private _acceptedFormatsList(): string {

        return Array.from(new Set(
            (this.acceptedTypes ?? [])
                .map((type) => FileUtil.getExtensionForType(type)?.toUpperCase())
                .filter((extension): extension is string => !!extension)
        )).join(", ");
    }

    /**
     * "SVG images" when the rejected type maps to a known extension, "image/svg+xml files"
     * when it doesn't - leading with a raw mime type reads worse than the type's plain name,
     * so the mapped case only falls back to the raw string when there's genuinely nothing
     * better to say.
     */
    private _formatRejectedType(type: string): string {

        const extension = FileUtil.getExtensionForType(type as MimeType)?.toUpperCase();
        return extension ? `${extension} images` : `${type} files`;
    }

    /**
     * One message builder for both rejection paths - selecting through the button and
     * pasting - so narrowing `acceptedTypes` improves the wording on both at once instead of
     * leaving one with a weaker message than the other.
     *
     * @param prefix - How the action reads, e.g. "Could not paste image" or "Could not select file"
     * @param type - The rejected file's own `type`
     */
    private _rejectionMessage(prefix: string, type: string): string {

        return `${prefix}: ${this._formatRejectedType(type)} are not supported. Accepted formats: ${this._acceptedFormatsList()}.`;
    }

    private _reportRejection(message: string): void {

        this._errorHandler.handleComponentError(new ComponentException(message));
        this._notificationService.displayErrorMessage(message);
    }
}
